import {
  AppWindow,
  Boxes,
  Building2,
  Hammer,
  ClipboardCheck,
  CreditCard,
  BarChart3,
  FileSearch,
  MonitorSmartphone,
  LayoutDashboard,
  LifeBuoy,
  Package,
  PackageCheck,
  Radar,
  ShieldCheck,
  Upload,
  ShoppingCart,
  UserCheck,
  Users,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PERMISSIONS, type Permission } from '@/auth/permissions';

/**
 * The navigation model.
 *
 * Every entry maps to a route that is backed by real endpoints. Modules the backend does not
 * implement (Android MDM) are deliberately absent: a navigation item leading to a screen with no
 * data behind it teaches users the product is broken.
 *
 * `anyOf` decides visibility. It mirrors the permissions the server enforces on the underlying
 * endpoints, so the menu offers only what the user could actually do.
 */
export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  anyOf: Permission[];
  /** Matches child routes for the active state, e.g. /assets/:id under /assets. */
  match?: string;
  description: string;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

export const NAVIGATION: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        label: 'Dashboard',
        to: '/dashboard',
        icon: LayoutDashboard,
        anyOf: [PERMISSIONS.ASSET_READ, PERMISSIONS.ASSET_READ_OWN],
        description: 'Fleet status and what needs attention',
      },
      {
        label: 'My assets',
        to: '/my-assets',
        icon: UserCheck,
        anyOf: [PERMISSIONS.ASSET_READ_OWN, PERMISSIONS.ACCEPTANCE_SUBMIT, PERMISSIONS.ASSET_READ],
        description: 'Devices assigned to you, and anything awaiting your acceptance',
      },
    ],
  },
  {
    id: 'assets',
    label: 'Assets',
    items: [
      {
        label: 'Registry',
        to: '/assets',
        icon: Boxes,
        match: '/assets',
        anyOf: [PERMISSIONS.ASSET_READ],
        description: 'Every asset, its lifecycle state and who holds it',
      },
      {
        label: 'Stocktakes',
        to: '/stocktakes',
        icon: ClipboardCheck,
        match: '/stocktakes',
        anyOf: [PERMISSIONS.ASSET_READ],
        description: 'Verify physical assets at a location and review missing items',
      },
      {
        label: 'Assignments',
        to: '/assignments',
        icon: ClipboardCheck,
        match: '/assignments',
        anyOf: [PERMISSIONS.ASSET_READ, PERMISSIONS.ASSET_ASSIGN],
        description: 'Who holds what, and what is awaiting acceptance',
      },
      {
        label: 'Preparation',
        to: '/preparation',
        icon: Wrench,
        match: '/preparation',
        anyOf: [PERMISSIONS.PREPARATION_READ, PERMISSIONS.PREPARATION_RUN],
        description: 'IT preparation runs and their task checklists',
      },
      {
        label: 'Consumables',
        to: '/consumables',
        icon: Package,
        match: '/consumables',
        anyOf: [PERMISSIONS.CONSUMABLE_READ],
        description: 'Stock counted by quantity, and what needs reordering',
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        label: 'Procurement',
        to: '/procurement',
        icon: ShoppingCart,
        match: '/procurement',
        anyOf: [PERMISSIONS.PROCUREMENT_READ, PERMISSIONS.PROCUREMENT_WRITE],
        description: 'Purchase orders, approvals and goods receipt',
      },
      {
        label: 'Endpoints',
        to: '/endpoints',
        icon: MonitorSmartphone,
        match: '/endpoints',
        anyOf: [PERMISSIONS.ENDPOINT_READ],
        description: 'Devices reporting through the agent, enrolment, and what is missing',
      },
      {
        label: 'Software licences', to: '/software-licences', icon: AppWindow, match: '/software-licences',
        anyOf: [PERMISSIONS.SOFTWARE_LICENCE_READ], description: 'Recorded entitlements and last-known device comparisons',
      },
      {
        label: 'Discovery',
        to: '/discovery',
        icon: Radar,
        match: '/discovery',
        anyOf: [PERMISSIONS.DISCOVERY_READ],
        description: 'What is on the network, and what the register cannot account for',
      },
    ],
  },
  {
    id: 'service',
    label: 'IT service',
    items: [
      {
        label: 'Service desk',
        to: '/service-desk',
        icon: LifeBuoy,
        match: '/service-desk',
        anyOf: [PERMISSIONS.TICKET_READ, PERMISSIONS.TICKET_READ_OWN],
        description: 'Incidents and service requests, linked to the assets they concern',
      },
      {
        label: 'Maintenance',
        to: '/maintenance',
        icon: Hammer,
        match: '/maintenance',
        anyOf: [PERMISSIONS.MAINTENANCE_READ],
        description: 'Preventive schedules, breakdown repairs, downtime and cost',
      },
      {
        label: 'Warranty & AMC',
        to: '/contracts',
        icon: ShieldCheck,
        match: '/contracts',
        anyOf: [PERMISSIONS.CONTRACT_READ],
        description: 'Warranties and maintenance contracts, what they cover, and renewals',
      },
    ],
  },
  {
    id: 'people',
    label: 'People',
    items: [
      {
        label: 'Employees',
        to: '/employees',
        icon: Users,
        match: '/employees',
        anyOf: [PERMISSIONS.EMPLOYEE_READ],
        description: 'The people assets are assigned to',
      },
    ],
  },
  {
    id: 'insight',
    label: 'Insight',
    items: [
      {
        label: 'Analytics',
        to: '/reports',
        icon: BarChart3,
        match: '/reports',
        anyOf: [PERMISSIONS.REPORT_READ],
        description: 'Fleet composition, acceptance outcomes, spend and coverage',
      },
      {
        label: 'Audit',
        to: '/audit',
        icon: FileSearch,
        match: '/audit',
        anyOf: [PERMISSIONS.AUDIT_READ],
        description: 'The tamper-evident record of everything that happened',
      },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      {
        label: 'Data import',
        to: '/imports',
        icon: Upload,
        match: '/imports',
        anyOf: [PERMISSIONS.IMPORT_READ, PERMISSIONS.IMPORT_VALIDATE],
        description: 'Load employees, assets and orders from CSV',
      },
      {
        label: 'Notifications',
        to: '/notifications',
        icon: PackageCheck,
        match: '/notifications',
        anyOf: [PERMISSIONS.NOTIFICATION_READ_OWN, PERMISSIONS.NOTIFICATION_ADMIN],
        description: 'Messages raised by the platform',
      },
    ],
  },
  {
    // The platform operator's section. Its permissions are held by no company role, so a company's
    // users never see it.
    id: 'platform',
    label: 'Platform',
    items: [
      {
        label: 'Companies',
        to: '/platform',
        icon: Building2,
        match: '/platform/tenants',
        anyOf: [PERMISSIONS.PLATFORM_READ],
        description: 'Every company, its licence, usage and billing',
      },
      {
        label: 'Plans',
        to: '/platform/plans',
        icon: CreditCard,
        match: '/platform/plans',
        anyOf: [PERMISSIONS.PLATFORM_READ],
        description: 'What companies can subscribe to',
      },
    ],
  },
];
